/**
 * BookPivot — "whose book am I looking at?"
 *
 * It is a VIEW FILTER over rows already loaded and already permitted — see
 * lib/book-scope.ts. So this component deliberately does NOT imply a permission
 * change: the label says "Viewing", not "Impersonating", and it says whose book
 * is on screen at all times so a manager can never mistake somebody else's
 * numbers for their own. That mistake is the whole risk of a pivot control, and
 * it is a UI problem rather than a security one.
 *
 * The advisor list comes from the loaded customers, not a user directory — only
 * names that actually appear on a row can match, so the list cannot offer a
 * choice that yields a confidently empty book.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * MULTI-SELECT (2026-09-08)
 *
 * Taylor: "ability to choose multiple csms or aes at the same time".
 *
 * This was a single <select>, so comparing two CSMs meant switching back and
 * forth and holding the first set of numbers in your head. It is now a
 * multi-pick over the same derived advisor list, and the selection is a UNION —
 * accounts EITHER selected advisor owns. Intersection would answer "which
 * accounts do they co-cover", a different and much rarer question, and getting
 * it silently would look like the filter was broken.
 *
 * Empty selection = everyone, which is why clearing the control widens the view
 * rather than blanking the page.
 *
 * WHO GETS WHICH CONTROL. Two separate rules, and conflating them was the
 * mistake to avoid (see book-scope.ts):
 *
 *   - ADD another advisor alongside your own book — everyone. Your book is
 *     still in the view, so you are not looking at someone else's numbers
 *     instead of your own.
 *   - REPLACE your book with somebody else's — manager level only
 *     (canPivotToOthers → manage:ae_routing). So a non-manager's selection
 *     always keeps them in it, and the component enforces that rather than
 *     trusting the caller.
 */
import type { Advisor } from '../lib/book-scope';
import { MultiSelect } from './MultiSelect';

export function BookPivot({
  advisors,
  value,
  selfName,
  canReplaceSelf,
  onChange,
}: {
  advisors: Advisor[];
  /** Selected advisor display names. Empty = everyone. */
  value: string[];
  /** The signed-in user's own name, so "mine" can be labelled as such. */
  selfName: string;
  /**
   * May this user view a book that does NOT include their own? Manager level.
   * When false, removing yourself from the selection is prevented here rather
   * than left to the caller to remember.
   */
  canReplaceSelf: boolean;
  onChange: (advisorNames: string[]) => void;
}) {
  const options = advisors.map((a) => a.name);
  const meta = new Map(advisors.map((a) => [a.name, a]));

  const includesSelf =
    !!selfName &&
    value.some((v) => v.trim().toLowerCase() === selfName.trim().toLowerCase());
  const viewingOthersOnly = value.length > 0 && !includesSelf;

  const apply = (next: string[]) => {
    /**
     * A non-manager may WIDEN their view but not swap it for somebody else's.
     * Enforced here because the alternative — hiding the control from
     * non-managers entirely, which is what the first version did — also removed
     * the legitimate "me plus my partner" case the multi-select exists for.
     *
     * Not a security boundary either way: every internal user can already see
     * every customer, and the customers list has always shown every AE's rows.
     */
    if (!canReplaceSelf && next.length > 0 && selfName) {
      const hasSelf = next.some(
        (v) => v.trim().toLowerCase() === selfName.trim().toLowerCase(),
      );
      if (!hasSelf) next = [selfName, ...next];
    }
    onChange(next);
  };

  return (
    <div className="book-pivot" data-testid="book-pivot">
      <span
        className="book-pivot-label"
        style={{ fontSize: 'var(--fs-base)', marginRight: 8 }}
      >
        Viewing
      </span>

      <MultiSelect
        onChange={apply}
        options={options}
        placeholder="Everyone"
        value={value}
      />

      {/* Own book in one click — the default and by far the commonest case,
          which a search-and-tick popover makes needlessly slow. */}
      {selfName && !(value.length === 1 && includesSelf) ? (
        <button
          className="pill"
          data-testid="book-pivot-mine"
          onClick={() => onChange([selfName])}
          style={{ marginLeft: 8 }}
          type="button"
        >
          Just mine
        </button>
      ) : null}

      {value.length > 0 ? (
        <button
          className="pill"
          data-testid="book-pivot-everyone"
          onClick={() => onChange([])}
          style={{ marginLeft: 6 }}
          type="button"
        >
          Everyone
        </button>
      ) : null}

      {/* Say whose numbers these are, always. A pivot whose state is ambiguous
          is worse than no pivot: the figures look like your own. */}
      {viewingOthersOnly ? (
        <span
          className="book-pivot-note"
          data-testid="book-pivot-viewing-other"
          style={{ marginLeft: 8, fontSize: 'var(--fs-base)' }}
        >
          Showing{' '}
          <strong>
            {value.length === 1 ? `${value[0]}’s` : `${value.length} advisors’`}
          </strong>{' '}
          book, not yours.
        </span>
      ) : null}

      {value.length === 0 ? (
        <span
          className="book-pivot-note"
          data-testid="book-pivot-viewing-all"
          style={{ marginLeft: 8, fontSize: 'var(--fs-base)' }}
        >
          Showing every customer.
        </span>
      ) : null}

      {value.length > 1 && includesSelf ? (
        <span
          className="book-pivot-note"
          data-testid="book-pivot-viewing-combined"
          style={{ marginLeft: 8, fontSize: 'var(--fs-base)' }}
        >
          Your book plus{' '}
          {value
            .filter(
              (v) => v.trim().toLowerCase() !== selfName.trim().toLowerCase(),
            )
            .map((v) => {
              const m = meta.get(v);
              return m ? `${v} (${m.customerCount})` : v;
            })
            .join(', ')}
          .
        </span>
      ) : null}
    </div>
  );
}

export default BookPivot;
